import {pruneQuery} from '@github-ui/filter-query'
import {isCustomPropertiesKey} from '@github-ui/repos-filter/providers'
import type {
  PropertyConfiguration,
  PropertySource,
  RepositoryPropertyParameters,
} from '@github-ui/repos-rules-types/rules-types'
import type {SuppliedFilterProvider} from '@github-ui/filter-core'

export function buildQueryForProperty(property: PropertyConfiguration, negationPrefix: string): string {
  const valueString = property.property_values.map(quoteIfNeeded).join(',')
  const queryKey = getPropertyQueryKey(property)
  return `${negationPrefix}${queryKey}:${valueString}`
}

const specialCharactersPattern = new RegExp(/[ ()',]/)
function quoteIfNeeded(value: string) {
  return specialCharactersPattern.test(value) ? `"${value}"` : value
}

export function buildQueryForAllProperties(properties: RepositoryPropertyParameters): string {
  const includeParts = properties.include.map(property => buildQueryForProperty(property, ''))
  const excludeParts = properties.exclude.map(property => buildQueryForProperty(property, '-'))

  return [...includeParts, ...excludeParts].join(' ')
}

export function queryToPropertyParameters(
  query: string,
  providers: SuppliedFilterProvider[],
  namespaces: string[],
): RepositoryPropertyParameters {
  const validTokens = pruneQuery(query, providers)

  const include: PropertyConfiguration[] = []
  const exclude: PropertyConfiguration[] = []

  for (const token of validTokens) {
    if (token.type === 'text') {
      continue
    }

    const property: PropertyConfiguration = {
      name: dropPropertyPrefix(token.key),
      source: getPropertySourceType(token.key, namespaces),
      property_values: token.values,
    }

    if (token.isNegated) {
      exclude.push(property)
    } else {
      include.push(property)
    }
  }

  return {include, exclude}
}

export function getPropertyQueryKey(property: PropertyConfiguration): string {
  // Both custom and external properties have a 'custom' source. They are distinguished by their name: external
  // properties carry a namespace prefix (e.g. 'azure.environment') so their name always contains a '.', whereas
  // custom property names never contain a '.'. Custom properties are not saved with the 'props.' prefix required to
  // build the query, so we add it for them only.
  const source = property.source || 'custom'
  if (source === 'custom' && !property.name.includes('.')) {
    return `props.${property.name}`
  }
  return property.name
}

function dropPropertyPrefix(key: string) {
  if (isCustomPropertiesKey(key)) {
    const [, ...segments] = key.split('.')
    const name = segments.join('.')
    return `${name}`
  }

  return key
}

function getPropertySourceType(key: string, namespaces: string[]): PropertySource {
  if (isCustomPropertiesKey(key)) return 'custom'

  // External properties are namespace-prefixed (e.g. 'azure.severity'), and the namespace prefix keeps them
  // distinguishable from true custom properties, whose names never contain a '.'.
  const [namespace, propertyName] = key.split('.')
  if (namespace && propertyName && namespaces.includes(namespace)) return 'custom'

  return 'system'
}
